import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, ArrowLeft, CheckCircle2, Upload, Plus, Trash2, Users, Loader2 } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PoliciesPage = () => {
  const { isHR } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reading, setReading] = useState(null); // policy being read
  const [numPages, setNumPages] = useState(0);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const scrollRef = useRef(null);
  // Memoize the react-pdf file prop: a new object each render makes <Document> reload and
  // destroys the worker transport while <Page>s render ("reading 'sendWithPromise'" crash).
  const readingId = reading?.id;
  const pdfFile = useMemo(
    () => (readingId ? { url: `${API_URL}/api/policies/${readingId}/file`, withCredentials: true } : null),
    [readingId]
  );

  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'General', description: '', file: null });
  const [uploading, setUploading] = useState(false);
  const [statsFor, setStatsFor] = useState(null);
  const [stats, setStats] = useState(null);

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/policies`, { withCredentials: true });
      setPolicies(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const openReader = (p) => {
    setReading(p); setNumPages(0);
    setScrolledToEnd(!!p.accepted); setAgreed(!!p.accepted);
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) setScrolledToEnd(true);
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API_URL}/api/policies/${reading.id}/accept`, {}, { withCredentials: true });
      toast.success('Policy accepted');
      setReading(null);
      fetchPolicies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept');
    } finally { setAccepting(false); }
  };

  const handleUpload = async () => {
    if (!form.title || !form.file) { toast.error('Title and PDF file are required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('category', form.category || 'General');
      fd.append('description', form.description || '');
      await axios.post(`${API_URL}/api/policies`, fd, { withCredentials: true });
      toast.success('Policy published');
      setUploadOpen(false);
      setForm({ title: '', category: 'General', description: '', file: null });
      fetchPolicies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this policy?')) return;
    try {
      await axios.delete(`${API_URL}/api/policies/${id}`, { withCredentials: true });
      toast.success('Policy removed');
      fetchPolicies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openStats = async (p) => {
    setStatsFor(p); setStats(null);
    try {
      const res = await axios.get(`${API_URL}/api/policies/${p.id}/acceptances`, { withCredentials: true });
      setStats(res.data);
    } catch { toast.error('Failed to load stats'); }
  };

  // ---------- Reader view ----------
  if (reading) {
    const progress = numPages ? 100 : 0;
    return (
      <div className="max-w-4xl mx-auto" data-testid="policy-reader">
        <button onClick={() => setReading(null)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="policy-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to Policies
        </button>
        <div className="flex items-center gap-3 mb-1">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-2xl font-bold">{reading.title}</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Please read the policy below carefully. You'll be able to accept it once you've scrolled through the entire document.</p>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
            <div className={`h-full transition-all duration-300 ${scrolledToEnd ? 'bg-green-500 w-full' : 'bg-primary w-1/3'}`} />
          </div>
          <span className={`text-sm font-semibold ${scrolledToEnd ? 'text-green-600' : 'text-muted-foreground'}`}>
            {scrolledToEnd ? 'Read ✓' : 'Keep scrolling…'}
          </span>
        </div>
        <div ref={scrollRef} onScroll={onScroll} className="border rounded-lg bg-muted/30 overflow-y-auto" style={{ height: '62vh' }} data-testid="policy-scroll">
          <Document
            file={pdfFile}
            onLoadSuccess={({ numPages }) => { setNumPages(numPages); if (numPages <= 1) setScrolledToEnd(true); }}
            onLoadError={() => toast.error('Could not load the PDF')}
            loading={<div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>}
          >
            {Array.from({ length: numPages }, (_, i) => (
              <div key={i} className="flex justify-center py-2">
                <Page pageNumber={i + 1} width={740} renderTextLayer={false} renderAnnotationLayer={false} />
              </div>
            ))}
          </Document>
        </div>
        {reading.accepted ? (
          <div className="mt-4 flex items-center gap-2 text-green-600" data-testid="policy-already-accepted">
            <CheckCircle2 className="w-5 h-5" /> You accepted this policy on {new Date(reading.accepted_at).toLocaleDateString()}.
          </div>
        ) : (
          <>
            <label className={`mt-4 flex items-start gap-3 rounded-lg border p-4 ${scrolledToEnd ? 'bg-primary/5 cursor-pointer' : 'opacity-50'}`}>
              <Checkbox checked={agreed} disabled={!scrolledToEnd} onCheckedChange={(v) => setAgreed(!!v)} data-testid="policy-agree-check" />
              <span className="text-sm">I have read and understood the <strong>{reading.title}</strong> and agree to comply with its provisions.</span>
            </label>
            <Button className="mt-4 w-full" disabled={!scrolledToEnd || !agreed || accepting} onClick={handleAccept} data-testid="policy-accept-btn">
              {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              I read and understood — Accept & Continue
            </Button>
          </>
        )}
      </div>
    );
  }

  // ---------- List view ----------
  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6 text-primary" /> Company Policies</h1>
          <p className="text-sm text-muted-foreground">Read each policy and confirm your acknowledgement.</p>
        </div>
        {isHR && (
          <Button onClick={() => setUploadOpen(true)} className="gap-2" data-testid="add-policy-btn"><Plus className="w-4 h-4" /> Add Policy</Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : policies.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">No policies published yet.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {policies.map(p => (
            <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`policy-card-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded">{p.category}</span>
                  {p.accepted
                    ? <span className="flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle2 className="w-4 h-4" /> Accepted</span>
                    : <span className="text-xs text-amber-600 font-medium">Action needed</span>}
                </div>
                <h3 className="font-semibold mt-3">{p.title}</h3>
                {p.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{p.description}</p>}
                {isHR && typeof p.accepted_count === 'number' && (
                  <button onClick={() => openStats(p)} className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary" data-testid={`policy-stats-${p.id}`}>
                    <Users className="w-3.5 h-3.5" /> {p.accepted_count}/{p.target_count} accepted
                  </button>
                )}
                <div className="flex items-center gap-2 mt-4">
                  <Button size="sm" variant={p.accepted ? 'outline' : 'default'} onClick={() => openReader(p)} className="flex-1" data-testid={`policy-read-${p.id}`}>
                    {p.accepted ? 'View' : 'Read & Accept'}
                  </Button>
                  {isHR && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} data-testid={`policy-delete-${p.id}`}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Policy</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g., Travel Policy" data-testid="policy-title-input" /></div>
            <div className="space-y-2"><Label>Category</Label><Input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder="Travel / Assets / HR" data-testid="policy-category-input" /></div>
            <div className="space-y-2"><Label>Short description (optional)</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="policy-desc-input" /></div>
            <div className="space-y-2"><Label>Policy PDF</Label><Input type="file" accept="application/pdf" onChange={e => setForm({ ...form, file: e.target.files?.[0] })} data-testid="policy-file-input" /></div>
            <Button className="w-full gap-2" onClick={handleUpload} disabled={uploading} data-testid="policy-upload-submit">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Publish Policy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats dialog */}
      <Dialog open={!!statsFor} onOpenChange={(o) => { if (!o) { setStatsFor(null); setStats(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{statsFor?.title} — Acceptance</DialogTitle></DialogHeader>
          {!stats ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
            <div className="space-y-4">
              <p className="text-sm"><strong className="text-green-600">{stats.accepted_count}</strong> of <strong>{stats.target_count}</strong> active employees accepted.</p>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Accepted</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {stats.accepted.length === 0 ? <p className="text-sm text-muted-foreground">None yet.</p> :
                    stats.accepted.map(a => <div key={a.id} className="text-sm flex justify-between"><span>{a.user_name}</span><span className="text-xs text-muted-foreground">{new Date(a.accepted_at).toLocaleDateString()}</span></div>)}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-1">Pending ({stats.pending.length})</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {stats.pending.length === 0 ? <p className="text-sm text-green-600">Everyone has accepted 🎉</p> :
                    stats.pending.map((e, i) => <div key={i} className="text-sm text-muted-foreground">{e.name}</div>)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PoliciesPage;
